import { Controller } from "@hotwired/stimulus";
import { i18n } from "../lib/i18n";

// Demo-only: animates the gated Neighbors mock. Four independent effects:
//  • AI summary — cycles the ai-loading-dots messages for ~1.5s, then smoothly
//    crossfades to the fabricated summary while the container grows to fit.
//  • Progress rings — each ring animates from 0 up to its data-ring-to --value
//    the first time it scrolls into view.
//  • Overdue banner — starts collapsed (max-h-0 / faded) and slides down to its
//    natural height after a short delay, drawing the eye to the behind goals.
//  • Add button — a faux finger-tap (press + ripple) on the "New Interaction"
//    plus button, then the interaction-type dropdown opens, as a closing flourish.
export default class extends Controller {
  static targets = ["aiLoading", "aiSummary", "aiBody", "ring", "overdue", "addButton", "dropdown"];
  static values = {
    delay: { type: Number, default: 1200 },
    ringDuration: { type: Number, default: 1100 },
    overdueDelay: { type: Number, default: 2000 },
    tapDelay: { type: Number, default: 3200 },
  };

  connect() {
    if (this.hasAiBodyTarget) this.startSummary();
    this.ringTargets.forEach((ring) => this.observeRing(ring));
    if (this.hasOverdueTarget) {
      this.overdueTimer = setTimeout(() => this.revealOverdue(), this.overdueDelayValue);
    }
    if (this.hasAddButtonTarget) {
      this.tapTimer = setTimeout(() => this.tapAdd(), this.tapDelayValue);
    }
  }

  disconnect() {
    clearInterval(this.tickTimer);
    clearTimeout(this.revealTimer);
    clearTimeout(this.fallbackTimer);
    clearTimeout(this.overdueTimer);
    clearTimeout(this.tapTimer);
    clearTimeout(this.rippleTimer);
    clearTimeout(this.openTimer);
    if (this.dismiss) document.removeEventListener("click", this.dismiss);
    this.observers?.forEach((o) => o.disconnect());
    cancelAnimationFrame(this.frame);
  }

  // ── AI summary ──────────────────────────────────────────────────────────────
  startSummary() {
    this.messages = [
      i18n.t("ai_loading_dots.thinking"),
      i18n.t("ai_loading_dots.analyzing"),
      i18n.t("ai_loading_dots.processing"),
      i18n.t("ai_loading_dots.summarizing"),
      i18n.t("ai_loading_dots.reviewing"),
      i18n.t("ai_loading_dots.generating"),
    ];
    this.dots = ["", ".", "..", "..."];
    this.messageIndex = 0;
    this.dotIndex = 0;
    this.dotCycles = 0;

    this.renderLoading();
    this.tickTimer = setInterval(() => this.tick(), 400);
    this.revealTimer = setTimeout(() => this.reveal(), this.delayValue);
  }

  tick() {
    this.dotIndex = (this.dotIndex + 1) % this.dots.length;
    if (this.dotIndex === 0 && ++this.dotCycles >= 2) {
      this.messageIndex = (this.messageIndex + 1) % this.messages.length;
      this.dotCycles = 0;
    }
    this.renderLoading();
  }

  renderLoading() {
    if (this.hasAiLoadingTarget) {
      this.aiLoadingTarget.textContent =
        this.messages[this.messageIndex] + this.dots[this.dotIndex];
    }
  }

  reveal() {
    clearInterval(this.tickTimer);
    const body = this.aiBodyTarget;
    const startHeight = body.offsetHeight;
    const targetHeight = this.aiSummaryTarget.offsetHeight;

    // Lock the current (loading) height so the height transition has a start.
    body.style.height = `${startHeight}px`;
    void body.offsetHeight; // force reflow

    // Overlapping crossfade + grow to the summary's height.
    this.aiLoadingTarget.style.opacity = "0";
    this.aiSummaryTarget.style.opacity = "1";
    body.style.height = `${targetHeight}px`;

    const finish = () => {
      if (this.finished) return;
      this.finished = true;
      body.removeEventListener("transitionend", onEnd);
      body.style.height = "auto"; // let it reflow naturally afterwards
      this.aiLoadingTarget.classList.add("hidden");
      this.aiSummaryTarget.classList.remove("absolute", "inset-x-0", "top-0");
    };
    const onEnd = (e) => {
      if (e.target === body && e.propertyName === "height") finish();
    };
    body.addEventListener("transitionend", onEnd);
    this.fallbackTimer = setTimeout(finish, 700);
  }

  // ── Progress rings ───────────────────────────────────────────────────────────
  observeRing(ring) {
    this.observers ||= [];
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          observer.disconnect();
          this.animateRing(ring);
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(ring);
    this.observers.push(observer);
  }

  // ── Overdue banner ───────────────────────────────────────────────────────────
  // Slides the collapsed (max-h-0 / faded) banner down to its natural height.
  revealOverdue() {
    this.overdueTargets.forEach((el) => {
      el.classList.remove("max-h-0", "opacity-0");
      el.classList.add("max-h-16", "opacity-100");
    });
  }

  // ── Add button ───────────────────────────────────────────────────────────────
  // Faux finger-tap the plus button, then open the interaction-type dropdown
  // just after so the press reads as the thing that triggered it.
  tapAdd() {
    this.tap(this.addButtonTarget);
    this.openTimer = setTimeout(() => this.openDropdown(), 160);
  }

  openDropdown() {
    if (!this.hasDropdownTarget) return;
    this.dropdownTarget.classList.add("dropdown-open");
    // It's force-opened via the class (not focus), which overrides daisyUI's
    // click-away close. Dismiss on the next click, then hand control back to
    // daisyUI's native focus behavior for any further opens.
    this.dismiss = () => {
      this.dropdownTarget.classList.remove("dropdown-open");
      document.removeEventListener("click", this.dismiss);
    };
    setTimeout(() => document.addEventListener("click", this.dismiss), 0);
  }

  // Faux finger-tap (press + ripple) — same treatment as the dashboard/survey mocks.
  tap(btn) {
    if (!btn) return;
    btn.classList.add("mock-tap");
    setTimeout(() => btn.classList.remove("mock-tap"), 350);

    const ripple = document.createElement("span");
    ripple.className = "mock-tap-ripple";
    btn.appendChild(ripple);
    this.rippleTimer = setTimeout(() => ripple.remove(), 550);
  }

  animateRing(ring) {
    const to = Number(ring.dataset.ringTo);
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
    let start = null;
    const step = (now) => {
      if (start === null) start = now;
      const progress = Math.min((now - start) / this.ringDurationValue, 1);
      ring.style.setProperty("--value", to * easeOutCubic(progress));
      if (progress < 1) this.frame = requestAnimationFrame(step);
    };
    this.frame = requestAnimationFrame(step);
  }
}
